import { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Loader2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import ReactMarkdown from 'react-markdown';

interface AIChatBubbleProps {
  portalType: "monument" | "partner" | "family";
}

type Message = { role: "user" | "assistant"; content: string };

const WELCOME_MESSAGES: Record<AIChatBubbleProps["portalType"], string> = {
  monument: "Hi! I can help you with the monument company portal. What would you like to know?",
  partner: "Hi there! Need help with orders, pricing, or anything else? Just ask!",
  family: "Hello. I'm here to help you with your loved one's memorial. What can I help with?",
};

const AIChatBubble = ({ portalType }: AIChatBubbleProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [streamingAnswer, setStreamingAnswer] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const streamingAnswerRef = useRef("");

  const handleClose = useCallback(() => {
    abortControllerRef.current?.abort();
    setIsOpen(false);
  }, []);

  const handleClearChat = useCallback(() => {
    abortControllerRef.current?.abort();
    setMessages([]);
    setQuestion("");
    setStreamingAnswer("");
    setError(null);
    setIsLoading(false);
    setTimeout(() => textareaRef.current?.focus(), 100);
  }, []);

  // Auto-scroll as messages or streaming answer update
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingAnswer, isLoading]);

  // Auto-focus textarea when dialog opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Escape key closes dialog
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleClose]);

  const handleSubmit = useCallback(async () => {
    const trimmed = question.trim();
    if (!trimmed || isLoading) return;

    // Add user message to history
    setMessages(prev => [...prev, { role: "user", content: trimmed }]);
    setQuestion("");
    setIsLoading(true);
    setError(null);
    setStreamingAnswer("");
    streamingAnswerRef.current = "";

    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    if (!token) {
      setError("Please sign in to use help.");
      setIsLoading(false);
      return;
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-chat`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
            "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({ portalType, question: trimmed }),
          signal: abortController.signal,
        }
      );

      if (!response.ok) {
        let errorMessage = "Something went wrong. Please try again.";
        try {
          const errBody = await response.json();
          errorMessage = errBody.error || errorMessage;
        } catch {
          // Response wasn't JSON
        }
        setError(errorMessage);
        setIsLoading(false);
        return;
      }

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop() || "";

        for (const chunk of chunks) {
          if (chunk.startsWith("event: error")) {
            const dataLine = chunk.split("\n").find(l => l.startsWith("data: "));
            if (dataLine) {
              try {
                const errData = JSON.parse(dataLine.slice(6));
                setError(errData.error || "Something went wrong.");
              } catch {
                setError("Something went wrong.");
              }
            }
          } else if (chunk.startsWith("data: [DONE]")) {
            // Stream complete
          } else if (chunk.startsWith("data: ")) {
            try {
              const data = JSON.parse(chunk.slice(6));
              if (data.text) {
                streamingAnswerRef.current += data.text;
                setStreamingAnswer(streamingAnswerRef.current);
              }
            } catch {
              // Skip unparseable chunks
            }
          }
        }
      }
    } catch (err) {
      if ((err as Error).name !== "AbortError") {
        setError("Connection lost. Please try again.");
      }
    }

    // Move completed answer into message history
    const finalAnswer = streamingAnswerRef.current;
    if (!abortController.signal.aborted && finalAnswer) {
      setMessages(prev => [...prev, { role: "assistant", content: finalAnswer }]);
    }
    setStreamingAnswer("");
    streamingAnswerRef.current = "";
    setIsLoading(false);
    abortControllerRef.current = null;
    setTimeout(() => textareaRef.current?.focus(), 100);
  }, [question, isLoading, portalType]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  // Floating bubble (closed state). The `bottom` position reads from a CSS
  // variable so any page rendering a sticky bottom action bar can lift the
  // bubble out of the way by setting `--chat-bottom` on documentElement.
  // Defaults to 1.5rem (matches the original bottom-6) when the var is unset.
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-brand-primary text-white shadow-[0_4px_20px_rgba(61,53,48,0.15)] transition-[bottom,transform] duration-300 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2"
        style={{ bottom: 'var(--chat-bottom, 1.5rem)' }}
        aria-label="Open help chat"
        title="Need help?"
      >
        <MessageCircle className="h-6 w-6" />
      </button>
    );
  }

  return (
    <div
      role="dialog"
      aria-label="Pontis Help"
      className="fixed right-6 z-50 flex w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-brand-header bg-brand-background shadow-[0_4px_20px_rgba(61,53,48,0.12)] animate-slide-up transition-[bottom] duration-300 md:w-[380px]"
      style={{ maxHeight: "min(500px, 70vh)", bottom: 'var(--chat-bottom, 1.5rem)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-brand-header bg-brand-header px-4 py-3">
        <h2 className="font-serif text-lg font-semibold text-brand-text-primary">
          Pontis Help
        </h2>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <button
              onClick={handleClearChat}
              className="flex h-8 w-8 items-center justify-center rounded-full text-brand-secondary transition-colors hover:bg-white/50 hover:text-brand-text-primary"
              aria-label="Start new conversation"
              title="Start over"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={handleClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-brand-secondary transition-colors hover:bg-white/50 hover:text-brand-text-primary"
            aria-label="Close help chat"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" style={{ minHeight: "200px" }}>
        {/* Welcome message */}
        <div className="flex justify-start">
          <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-brand-header px-4 py-3 text-sm text-brand-text-primary">
            {WELCOME_MESSAGES[portalType]}
          </div>
        </div>

        {/* Message history */}
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            {msg.role === "user" ? (
              <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-brand-primary px-4 py-3 text-sm text-white">
                {msg.content}
              </div>
            ) : (
              <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-brand-header px-4 py-3 text-sm text-brand-text-primary prose prose-sm prose-stone max-w-none prose-headings:text-base prose-headings:font-semibold prose-headings:mt-3 prose-headings:mb-1 prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5">
                <ReactMarkdown>{msg.content}</ReactMarkdown>
              </div>
            )}
          </div>
        ))}

        {/* Currently streaming answer */}
        {(streamingAnswer || isLoading) && (
          <div className="flex justify-start">
            <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-brand-header px-4 py-3 text-sm text-brand-text-primary prose prose-sm prose-stone max-w-none prose-headings:text-base prose-headings:font-semibold prose-headings:mt-3 prose-headings:mb-1 prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5">
              {streamingAnswer ? (
                <ReactMarkdown>{streamingAnswer}</ReactMarkdown>
              ) : (
                <div className="flex items-center gap-2 text-brand-secondary">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Thinking...</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="flex justify-start">
            <div className="max-w-[85%] rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area — always visible */}
      <div className="border-t border-brand-header px-4 py-3">
        <div className="flex items-end gap-2">
          <Textarea
            ref={textareaRef}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask a question..."
            disabled={isLoading}
            className="min-h-[40px] max-h-[80px] resize-none rounded-xl border-brand-header bg-white text-sm focus-visible:ring-brand-primary"
            rows={1}
            maxLength={1000}
          />
          <Button
            onClick={handleSubmit}
            disabled={!question.trim() || isLoading}
            size="icon"
            className="h-10 w-10 shrink-0 rounded-full bg-brand-primary hover:bg-brand-primary/90"
            aria-label="Send question"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
        <p className="mt-1 text-xs text-brand-secondary">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>
  );
};

export default AIChatBubble;
